import React, { useCallback, useEffect, useMemo, useState } from 'react';
import VariousDocuments from './createInvitation/step2/VariousDocuments/VariousDocuments';
import FillingDocuments from './createInvitation/step2/FillingDocuments/FillingDocuments';
import { DocumentCopyItem } from './createInvitation/step2/VariousDocuments/types';
import {
  findIncompleteEmployerForms,
  formatIncompleteEmployerFormsMessage,
  normalizeFormCode,
  pruneEmployerContext,
  type FormEmployerContext,
} from './createInvitation/invitationEmployerFields';
import {
  fetchInvitationForEdit,
  lockReasonLabel,
  splitInvitationRecipient,
  updateInvitation,
  type InvitationEditDto,
} from '../../../../services/invitationEditApi';
import backButtonIcon from '../../../../assests/icons/back-button.png';
import successIcon from '../../../../assests/icons/success.png';
import failIcon from '../../../../assests/icons/fail.png';
import './createInvitation/step2/CreateInvitationStep2.css';
import './EditInvitationPanel.css';

export interface EditInvitationPanelProps {
  invitationId: string;
  focusEmail: string;
  focusName: string;
  onBack: () => void;
  onSaved: () => void;
  onOpenMessages?: (email: string, invitationId?: string) => void;
}

function recipientStatusLabel(status: string): string {
  switch (status) {
    case 'sent':
      return '未開封';
    case 'draft':
      return '進行中';
    case 'resubmission_requested':
      return '再提出依頼中';
    case 'submitted':
      return '完了';
    case 'resubmitted':
      return '再提出済み';
    case 'cleared':
      return '審査完了';
    default:
      return status;
  }
}

function sameStringSet(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const setA = new Set(a);
  return b.every((code) => setA.has(code));
}

function toDocumentItems(
  assignments: InvitationEditDto['documents']
): { items: DocumentCopyItem[]; locked: Record<string, string> } {
  const locked: Record<string, string> = {};
  const items = assignments.map((row) => {
    if (row.locked && row.lockReason) {
      locked[row.code] = lockReasonLabel(row.lockReason);
    }
    return {
      id: row.code,
      nameJp: row.nameJa,
      checked: row.selected,
    };
  });
  return { items, locked };
}

function toFormItems(
  assignments: InvitationEditDto['forms']
): { items: DocumentCopyItem[]; locked: Record<string, string> } {
  const locked: Record<string, string> = {};
  const items = assignments.map((row) => {
    if (row.locked && row.lockReason) {
      locked[row.code] = lockReasonLabel(row.lockReason);
    }
    return {
      id: row.code,
      nameJp: row.nameJa,
      checked: row.selected,
    };
  });
  return { items, locked };
}

const EditInvitationPanel: React.FC<EditInvitationPanelProps> = ({
  invitationId,
  focusEmail,
  focusName,
  onBack,
  onSaved,
  onOpenMessages,
}) => {
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [editData, setEditData] = useState<InvitationEditDto | null>(null);
  const [deadline, setDeadline] = useState('');
  const [selectedDocuments, setSelectedDocuments] = useState<string[]>([]);
  const [selectedFormCodes, setSelectedFormCodes] = useState<string[]>([]);
  const [formEmployerContext, setFormEmployerContext] = useState<FormEmployerContext>({});
  const [docLockedCodes, setDocLockedCodes] = useState<Record<string, string>>({});
  const [formLockedCodes, setFormLockedCodes] = useState<Record<string, string>>({});
  const [variousDocumentsOpen, setVariousDocumentsOpen] = useState(true);
  const [fillingDocumentsOpen, setFillingDocumentsOpen] = useState(true);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [showSharedConfirm, setShowSharedConfirm] = useState(false);
  const [showSplitProgressConfirm, setShowSplitProgressConfirm] = useState(false);
  const [resultMessage, setResultMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [initialSnapshot, setInitialSnapshot] = useState<{
    deadline: string;
    docCodes: string[];
    formCodes: string[];
  } | null>(null);

  const loadEdit = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    const data = await fetchInvitationForEdit(invitationId);
    if (!data) {
      setLoadError('招待情報を読み込めませんでした。');
      setEditData(null);
      setLoading(false);
      return;
    }
    const docs = toDocumentItems(data.documents);
    const forms = toFormItems(data.forms);
    const initialDocCodes = docs.items.filter((i) => i.checked).map((i) => i.id);
    const initialFormCodes = forms.items.filter((i) => i.checked).map((i) => i.id);
    setEditData(data);
    setDeadline(data.deadline || '');
    setSelectedDocuments(initialDocCodes);
    setSelectedFormCodes(initialFormCodes);
    setInitialSnapshot({
      deadline: data.deadline || '',
      docCodes: initialDocCodes,
      formCodes: initialFormCodes,
    });
    setFormEmployerContext(
      pruneEmployerContext(initialFormCodes, data.formEmployerContext ?? {})
    );
    setDocLockedCodes(docs.locked);
    setFormLockedCodes(forms.locked);
    setLoading(false);
  }, [invitationId]);

  useEffect(() => {
    loadEdit();
  }, [loadEdit]);

  const docItems = useMemo(() => {
    if (!editData) return undefined;
    return editData.documents.map((row) => ({
      id: row.code,
      nameJp: row.nameJa,
      checked: selectedDocuments.includes(row.code),
    }));
  }, [editData, selectedDocuments]);

  const formItems = useMemo(() => {
    if (!editData) return undefined;
    return editData.forms.map((row) => ({
      id: row.code,
      nameJp: row.nameJa,
      checked: selectedFormCodes.includes(row.code),
    }));
  }, [editData, selectedFormCodes]);

  const incompleteEmployerForms = findIncompleteEmployerForms(
    selectedFormCodes,
    formEmployerContext
  );

  const assignmentsUnchanged =
    initialSnapshot != null &&
    sameStringSet(selectedDocuments, initialSnapshot.docCodes) &&
    sameStringSet(selectedFormCodes, initialSnapshot.formCodes);

  const deadlineChanged =
    initialSnapshot != null && deadline !== initialSnapshot.deadline;

  const isDeadlineOnlyUpdate =
    assignmentsUnchanged &&
    deadlineChanged &&
    (initialSnapshot?.docCodes.length ?? 0) + (initialSnapshot?.formCodes.length ?? 0) > 0;

  const hasAssignments = selectedDocuments.length > 0 || selectedFormCodes.length > 0;

  const canSave = Boolean(
    editData?.editable &&
    deadline &&
    (isDeadlineOnlyUpdate || hasAssignments)
  );

  const buildPayload = () => ({
    deadline,
    docCodes: selectedDocuments,
    formCodes: selectedFormCodes,
    formEmployerContext: pruneEmployerContext(selectedFormCodes, formEmployerContext),
  });

  const handleSaveClick = () => {
    if (!canSave || !editData) return;
    setValidationError(null);
    if (editData.sharedInvitation) {
      setShowSharedConfirm(true);
      return;
    }
    void performUpdate(false);
  };

  const handleSplitClick = async () => {
    if (!canSave || !editData?.sharedInvitation) return;
    setValidationError(null);
    setSaving(true);
    const result = await splitInvitationRecipient(invitationId, {
      email: focusEmail,
      ...buildPayload(),
      acknowledgeProgressLoss: false,
    });
    setSaving(false);
    if (!result.ok) {
      if (result.error.includes('acknowledgeProgressLoss')) {
        setShowSplitProgressConfirm(true);
        return;
      }
      setResultMessage({ type: 'error', text: result.error });
      return;
    }
    setResultMessage({
      type: 'success',
      text: `${focusName}さんを新しい招待（${result.newInvitationId}）に分離しました。`,
    });
  };

  const performUpdate = async (confirmShared: boolean) => {
    if (!editData) return;
    setSaving(true);
    setShowSharedConfirm(false);
    const result = await updateInvitation(invitationId, {
      ...buildPayload(),
      confirmSharedUpdate: confirmShared,
    });
    setSaving(false);
    if (!result.ok) {
      if (result.error.includes('confirmSharedUpdate')) {
        setShowSharedConfirm(true);
        return;
      }
      setResultMessage({ type: 'error', text: result.error });
      return;
    }
    setResultMessage({ type: 'success', text: result.message });
  };

  const performSplit = async (acknowledgeProgressLoss: boolean) => {
    if (!editData) return;
    setSaving(true);
    setShowSplitProgressConfirm(false);
    const result = await splitInvitationRecipient(invitationId, {
      email: focusEmail,
      ...buildPayload(),
      acknowledgeProgressLoss,
    });
    setSaving(false);
    if (!result.ok) {
      if (result.error.includes('acknowledgeProgressLoss')) {
        setShowSplitProgressConfirm(true);
        return;
      }
      setResultMessage({ type: 'error', text: result.error });
      return;
    }
    setResultMessage({
      type: 'success',
      text: `${focusName}さんを新しい招待（${result.newInvitationId}）に分離しました。`,
    });
  };

  const handleFormEmployerContextChange = (formCode: string, fields: Record<string, string>) => {
    const normalized = normalizeFormCode(formCode);
    if (!normalized) return;
    setValidationError(null);
    setFormEmployerContext((prev) => ({ ...prev, [normalized]: fields }));
  };

  const handleFormEmployerContextClear = (formCode: string) => {
    const normalized = normalizeFormCode(formCode);
    if (!normalized) return;
    setFormEmployerContext((prev) => {
      if (!prev[normalized]) return prev;
      const next = { ...prev };
      delete next[normalized];
      return next;
    });
  };

  const handleSelectedFormCodesChange = (formCodes: string[]) => {
    setValidationError(null);
    setSelectedFormCodes(formCodes);
    setFormEmployerContext((prev) => pruneEmployerContext(formCodes, prev));
  };

  if (loading) {
    return (
      <div className="edit-invitation-panel">
        <p className="edit-invitation-panel__loading">読み込み中…</p>
      </div>
    );
  }

  if (loadError || !editData) {
    return (
      <div className="edit-invitation-panel">
        <div className="edit-invitation-panel__header">
          <button type="button" className="edit-invitation-panel__back-btn" onClick={onBack} aria-label="戻る">
            <img src={backButtonIcon} alt="" className="edit-invitation-panel__back-icon" aria-hidden />
          </button>
          <h1 className="edit-invitation-panel__title">招待を編集</h1>
        </div>
        <p className="edit-invitation-panel__error" role="alert">{loadError}</p>
      </div>
    );
  }

  return (
    <div className="edit-invitation-panel">
      {resultMessage && (
        <div className="edit-invitation-panel__result-overlay" role="dialog" aria-modal="true">
          <div className="edit-invitation-panel__result-popup">
            <img
              src={resultMessage.type === 'success' ? successIcon : failIcon}
              alt=""
              className="edit-invitation-panel__result-icon"
              aria-hidden
            />
            <p className="edit-invitation-panel__result-text">{resultMessage.text}</p>
            <button
              type="button"
              className="edit-invitation-panel__result-close-btn"
              onClick={() => {
                setResultMessage(null);
                if (resultMessage.type === 'success') {
                  onSaved();
                }
              }}
            >
              閉じる
            </button>
          </div>
        </div>
      )}

      {showSharedConfirm && (
        <div className="edit-invitation-panel__confirm-overlay" role="dialog" aria-modal="true">
          <div className="edit-invitation-panel__confirm-popup">
            <h2>共通招待の更新</h2>
            <p>
              この招待は {editData.recipientCount} 名の対象者に共通です。
              {isDeadlineOnlyUpdate
                ? '提出期限の変更が対象者に反映されます（メールは再送されません）。'
                : '保存すると、すべての対象者の書類一覧が更新されます。メールは再送されません。'}
            </p>
            <ul className="edit-invitation-panel__recipient-list">
              {editData.recipients.map((r) => (
                <li key={r.email}>{r.name}（{r.email}）</li>
              ))}
            </ul>
            <div className="edit-invitation-panel__confirm-actions">
              <button type="button" onClick={() => setShowSharedConfirm(false)}>キャンセル</button>
              <button type="button" onClick={() => void performUpdate(true)} disabled={saving}>
                全員に反映して保存
              </button>
            </div>
          </div>
        </div>
      )}

      {showSplitProgressConfirm && (
        <div className="edit-invitation-panel__confirm-overlay" role="dialog" aria-modal="true">
          <div className="edit-invitation-panel__confirm-popup">
            <h2>個別分離の確認</h2>
            <p>
              {focusName}さんはすでに提出・記入を開始している可能性があります。
              分離すると、新しい招待IDに移動し、既存の進捗は引き継がれません。
            </p>
            <div className="edit-invitation-panel__confirm-actions">
              <button type="button" onClick={() => setShowSplitProgressConfirm(false)}>キャンセル</button>
              <button
                type="button"
                onClick={() => void performSplit(true)}
                disabled={saving}
              >
                分離して保存
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="edit-invitation-panel__header">
        <button type="button" className="edit-invitation-panel__back-btn" onClick={onBack} aria-label="戻る">
          <img src={backButtonIcon} alt="" className="edit-invitation-panel__back-icon" aria-hidden />
        </button>
        <h1 className="edit-invitation-panel__title">招待を編集 — {focusName}</h1>
        {onOpenMessages && focusEmail.trim() && (
          <button
            type="button"
            className="edit-invitation-panel__message-btn"
            onClick={() => onOpenMessages(focusEmail.trim(), invitationId)}
          >
            メッセージ
          </button>
        )}
      </div>

      <div className="edit-invitation-panel__meta">
        <p>招待ID: {editData.invitationId}</p>
        <p>対象者メール: {focusEmail}</p>
        {editData.recipients.length > 1 && (
          <div className="edit-invitation-panel__recipients">
            <p className="edit-invitation-panel__recipients-title">この招待の対象者:</p>
            <ul className="edit-invitation-panel__recipients-list">
              {editData.recipients.map((r) => (
                <li key={r.email}>
                  {r.name}（{r.email}）— {recipientStatusLabel(r.status)}
                  {r.status === 'cleared' && (
                    <span className="edit-invitation-panel__recipients-note"> ※編集判定の対象外</span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {!editData.editable && (
        <div className="edit-invitation-panel__banner edit-invitation-panel__banner--error" role="alert">
          {editData.editBlockReason ?? 'この招待は編集できません。'}
        </div>
      )}

      <div className="edit-invitation-panel__banner edit-invitation-panel__banner--info">
        <strong>編集ルール</strong>
        <ul>
          <li>提出期限のみ変更する場合は、書類の選択を変えなくても「保存」できます。</li>
          <li>提出済み・アップロード済み・記入済みの書類は削除できません（ロック表示）。</li>
          <li>保存時にメールは送信されません。従業員には画面内のお知らせが表示されます。</li>
          <li>編集できるのは「未開封」「進行中」「再提出依頼中」の対象者です（「審査完了」は判定対象外）。</li>
          <li>「提出済み」「再提出済み」の対象者が同一招待にいる場合は編集できません。</li>
        </ul>
      </div>

      {editData.sharedInvitation && editData.editable && (
        <div className="edit-invitation-panel__banner edit-invitation-panel__banner--warning">
          この招待は {editData.recipientCount} 名共通です。通常の保存は全員の招待内容に反映されます（審査完了の対象者にはお知らせは送られません）。
          この対象者だけ別内容にする場合は「個別に分離して保存」を使用してください。
        </div>
      )}

      <div className="edit-invitation-panel__deadline-section">
        <div className="edit-invitation-panel__deadline-row">
          <label className="edit-invitation-panel__deadline-label" htmlFor="edit-invitation-deadline">
            提出期限
          </label>
          <div className="edit-invitation-panel__date-wrap">
            <input
              id="edit-invitation-deadline"
              type="date"
              className={`edit-invitation-panel__date-input${
                editData.editable ? '' : ' edit-invitation-panel__date-input--disabled'
              }`}
              value={deadline}
              onChange={(e) => setDeadline(e.target.value)}
              disabled={!editData.editable}
              aria-label="提出期限"
            />
          </div>
        </div>
        <p className="edit-invitation-panel__deadline-hint">
          日付を変更して「保存」を押してください。書類の追加・削除がなくても保存できます（メールは送信されません）。
        </p>
      </div>

      <div className="edit-invitation-panel__content create-invitation-step2__content">
        <VariousDocuments
          isOpen={variousDocumentsOpen}
          onToggle={() => setVariousDocumentsOpen((v) => !v)}
          value={docItems}
          onChange={(items) => setSelectedDocuments(items.filter((i) => i.checked).map((i) => i.id))}
          lockedCodes={docLockedCodes}
          allowCatalogEdit={false}
        />
        <FillingDocuments
          isOpen={fillingDocumentsOpen}
          onToggle={() => setFillingDocumentsOpen((v) => !v)}
          value={formItems}
          onChange={(items) =>
            handleSelectedFormCodesChange(items.filter((i) => i.checked).map((i) => i.id))
          }
          employerContext={formEmployerContext}
          onEmployerContextChange={handleFormEmployerContextChange}
          onEmployerContextClear={handleFormEmployerContextClear}
          lockedCodes={formLockedCodes}
          allowCatalogEdit={false}
        />
      </div>

      <div className="edit-invitation-panel__footer">
        <button type="button" className="edit-invitation-panel__back-footer-btn" onClick={onBack}>
          招待へ
        </button>
        <div className="edit-invitation-panel__footer-right">
          {validationError && (
            <p className="edit-invitation-panel__validation-error" role="alert">{validationError}</p>
          )}
          {editData.editable && incompleteEmployerForms.length > 0 && (
            <p className="edit-invitation-panel__validation-hint">
              {formatIncompleteEmployerFormsMessage(incompleteEmployerForms)}
            </p>
          )}
          {editData.sharedInvitation && editData.editable && (
            <button
              type="button"
              className="edit-invitation-panel__split-btn"
              onClick={handleSplitClick}
              disabled={!canSave || saving}
            >
              個別に分離して保存
            </button>
          )}
          <button
            type="button"
            className="edit-invitation-panel__save-btn"
            onClick={handleSaveClick}
            disabled={!canSave || saving}
          >
            {saving ? '保存中…' : '保存'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditInvitationPanel;
